import { AnyObj, getFileName, getSuffix, isIOS, isInWeChat } from './index'
import axios from 'axios'
import { trackWechatInstallApp } from '@/utils/wechat-install-sensors'

/**
* 防抖
* @param {Function} func 回调函数
* @param {number} waitTime 防抖时间(毫秒)
* @param {boolean} firstIsDebounce 第一次是否防抖，默认true
* @returns {Function} 转换后的函数
*/
let defaultWaitTime = 300 // 默认防抖时间（ms）
export function debounce<F extends(...args: any) => any>(func: F, waitTime?: number, firstIsDebounce?: boolean): (...args: Parameters<F>) => void {
  if (waitTime === undefined) {
    waitTime = defaultWaitTime
  }
  if (firstIsDebounce === undefined) {
    firstIsDebounce = true
  }
  let timeout: any
  return function(...args: any[]) {
    if (timeout && typeof Number(timeout) === 'number') {
      clearTimeout(timeout)
    }
    if (firstIsDebounce) {
      timeout = setTimeout(() => {
        func(...args)
      }, waitTime)
    } else {
      func(...args)
      firstIsDebounce = true
    }
  }
}

/**
* 除去多余参数
* @param {AnyObj} obj 需要处理的对象
* @param {boolean} isFillEmptyStr 是否填充为空字符串
* @returns {object} 处理完成后的对象
*/
export function deleteEmpty(obj: AnyObj, isFillEmptyStr?: boolean): AnyObj {
  if (isFillEmptyStr === undefined) {
    isFillEmptyStr = false
  }
  obj = JSON.parse(JSON.stringify(obj))
  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].trim()
    }
    if (obj[key] === undefined || obj[key] === null || obj[key] === '') {
      if (isFillEmptyStr) {
        obj[key] = ''
      } else {
        delete obj[key]
      }
    }
    if (typeof obj[key] === 'object' && obj[key].constructor !== Array) {
      obj[key] = deleteEmpty(obj[key], isFillEmptyStr)
    }
  })
  return obj
}

/**
* 根据 blob 或 url 直接保存文件
* @param {string|Blob} url 获取文件对应的URL
* @param {string} [fileName] 文件名，仅对同源 url 生效。可以直接带后缀，这样就可以免除第三个参数
* @param {string} [fileType] 文件类型
*/
export function saveFile(url: Blob | string, fileName?: string, fileType?: string): void {
  let isBlob = url instanceof Blob
  let targetUrl: string
  if (url instanceof Blob) {
    targetUrl = URL.createObjectURL(url)
  } else {
    targetUrl = url
  }
  trackWechatInstallApp(targetUrl, fileType === 'apk' || fileType === 'ipa')
  let a = document.createElement('a')
  let fileSaveName = fileName ? [fileName, fileType].filter(Boolean).join('.') : ''
  fileSaveName && (a.download = fileSaveName)
  a.href = targetUrl
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.parentNode && a.parentNode.removeChild(a)
  if (isBlob) {
    URL.revokeObjectURL(targetUrl)
  }
}

/**
* 保存远程文件
* @param {string} url 获取文件对应的URL
* @param {object} data 文件内容
* @param {string} [fileName] 文件名，仅对同源 url 生效。可以直接带后缀，这样就可以免除第三个参数
* @param {string} [fileType] 文件类型
*/
export function saveRemoteFile(url: string, data?: unknown, fileName?: string, fileType?: string): void {
  if (data === undefined) {
    data = {}
  }
  fileName = fileName || getFileName(url)
  fileType = fileType || getSuffix(url)
  data = Object.assign({}, data, { token: localStorage.getItem('token') })
  axios({
    url,
    method: 'get',
    responseType: 'blob',
    params: data
  }).then(res => {
    let fileBlob = new Blob([res.data]) // 创建一个Blob对象
    saveFile(fileBlob, fileName, fileType)
  })
}

interface FillZeroOptions {
  isFillBack?: boolean;
  truncate?: boolean;
}

/**
 * 字符串填充「0」
 * @param {string | number} origin 原始字符串
 * @param {number} length 目标字符串长度
 * @param {FillZeroOptions} options 其他参数
 * @param {boolean} [options.isFillBack=false] 是否从尾部填充，默认从头部填充
 * @param {boolean} [options.truncate=false] 超出时是否截断
 * @returns {string} 处理后的字符串
 */
export function fillZero(origin: string | number, length: number, options?: FillZeroOptions): string {
  const { isFillBack = false, truncate = false } = options || {}

  let originStr = String(origin)
  if (!truncate) {
    // 若原长度比目标长度更长，且 truncate 为 false，则不截断
    length = originStr.length > length ? originStr.length : length
  }
  if (isFillBack) {
    return (originStr + new Array(length).join('0')).substring(0, length)
  } else {
    return (new Array(length).join('0') + origin.toString()).slice(-length)
  }
}

/**
 * 获取随机字符串
 * @param {number} length 字符串长度
 * @param {string} [type] 随机字符串类型，letter纯字母，number纯数字，mix字母与数字混合
 * @param {string} [prefix] 字符串前缀
 * @returns {string} 生成的随机字符串
 */
export function randomString(length: number, type?: string, prefix?: string): string {
  if (type === undefined) {
    type = 'letter'
  }
  if (prefix === undefined) {
    prefix = ''
  }
  let numbers = '0123456789'
  let letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  let chars = ''
  let result = prefix
  if (type === 'number' || type === 'mix') {
    chars += numbers
  }
  if (type === 'letter' || type === 'mix') {
    chars += letters
  }
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}

/**
* 兼容设置页面title
* @param {string} titleText title文案
*/
export function setTitle(titleText: string): void {
  document.title = titleText
  if (isIOS || isInWeChat) {
    let $iframe = document.createElement('iframe')
    $iframe.title = titleText
    $iframe.style.opacity = '0'
    document.body.appendChild($iframe)
    document.body.removeChild($iframe)
  }
}

/** 
 * 安全解码 
 * @param {string} str 待解码字符串
 * @returns {any} 解码后的数据
 */
export const safeDecodeJson = (str: string | null | undefined) => {
  if (!str || typeof str !== 'string') {
    return null
  }
  try {
    return JSON.parse(str)
  } catch {
    try {
      return JSON.parse(decodeURIComponent(str))
    } catch {
      try {
        // 处理双层编码
        return JSON.parse(decodeURIComponent(decodeURIComponent(str)))
      } catch {
        return null
      }
    }
  }
}
